import { useState, useEffect, useCallback, useMemo, type ChangeEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AdManager } from '@/components/admin/AdManager';
import { SkinsManager } from '@/components/admin/SkinsManager';
import { WalletCardsManager } from '@/components/admin/WalletCardsManager';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { ChevronLeft, Upload, Plus, Save, Image as ImageIcon, Settings, Trash2, Package, Activity, DollarSign, Users, Gavel, ArrowUpRight, TrendingUp, Clock, RefreshCw, PieChart, Wallet, ShieldAlert, BarChart3, Database, HardDrive, Send, LayoutGrid, Share2, Search, ArrowRightLeft, Percent, Eye, ChevronUp, ChevronDown, X, ExternalLink, Info, Hash, Layout, Gift } from 'lucide-react';
import { UISettingsManager } from '@/components/admin/UISettingsManager';
import { ControllerManager } from '@/components/admin/ControllerManager';
import { SystemMap } from '@/components/admin/SystemMap';
import { CardRenderer } from '@/components/Cards/CardRenderer';
import { useNotify } from '@/context/NotificationContext';
import { levelApi, botWalletApi, adminApi } from '@/utils/api';
import { RecordsManager } from '@/components/admin/RecordsManager';
import { SubscribersManager } from '@/components/admin/SubscribersManager';
import { ConfirmActionModal } from '@/components/Modals/ConfirmActionModal';
import { PromptModal } from '@/components/ui/PromptModal';
import { GiftManager } from '@/components/admin/GiftManager';
import { LevelManager } from '@/components/admin/LevelManager';
import { BotWalletPanel } from '@/components/admin/BotWalletPanel';
import { FundsManager } from '@/components/admin/FundsManager';
import { AuctionMonitor } from '@/components/admin/AuctionMonitor';
import { FinancialAudit } from '@/components/admin/FinancialAudit';
import { TableViewer } from '@/components/admin/TableViewer';
import { InfoUsersPanel } from '@/components/admin/InfoUsersPanel';
import { GlobalBroadcastManager } from '@/components/admin/GlobalBroadcastManager';

interface CardDefinition {
    id: string;
    file: File | null;
    previewUrl: string;
    name: string;
    rarity: number; // 0-100
    rank: string;
    level: number;
    color: string;
    textColor: string;
    quantity: number;
    isFree: boolean;
    price: number;
    // New Fields
    listingType: string;
    royaltyFee: number;
    isTradable: boolean;
    accessLevel: number;
    dropRate: number;
    iconColor: string;
    specialFx: string;
    variantGroupId: string | number;
    skinLabel: string;
}


const FullAuditView = ({ log, onClose }: { log: any; onClose: () => void }) => {
    const [relatedLogs, setRelatedLogs] = useState<any[]>([]);
    const [loadingRelated, setLoadingRelated] = useState(false);

    const metadata = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
    const dealId = metadata?.dealId || metadata?.deal_id;
    const listingId = metadata?.listingId;
    const cardId = metadata?.cardId || metadata?.card_id;

    useEffect(() => {
        if (dealId || listingId || cardId) {
            setLoadingRelated(true);
            adminApi.getRelatedLogs({ dealId, listingId, cardId })
                .then(res => res.json())
                .then(data => {
                    if (data.success) setRelatedLogs(data.logs);
                })
                .finally(() => setLoadingRelated(false));
        } else {
            // Include the log itself if no related items found to keep the UI consistent
            setRelatedLogs([log]);
        }
    }, [dealId, listingId, cardId, log]);

    const formatGRAM = (val: any) => {
        const num = parseFloat(val);
        return isNaN(num) ? '0' : parseFloat(num.toFixed(9)).toString();
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[200] bg-[#0d0d12] flex flex-col items-center overflow-y-auto"
        >
            {/* Header / Nav */}
            <div className="sticky top-0 w-full z-10 bg-[#0d0d12]/90 backdrop-blur-xl border-b border-white/5 px-6 py-4 flex items-center justify-between shadow-2xl">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onClose}
                        className="p-3 bg-white/5 hover:bg-white/10 rounded-2xl text-white transition-all flex items-center justify-center group"
                    >
                        <ChevronLeft size={24} className="group-hover:-translate-x-1 transition-transform" />
                    </button>
                    <div>
                        <div className="flex items-center gap-2">
                            <ShieldAlert size={14} className="text-blue-500" />
                            <h1 className="text-xl font-black text-white uppercase tracking-wider">Mission Critical Audit</h1>
                        </div>
                        <div className="text-[10px] text-white/40 font-mono mt-1 flex items-center gap-2">
                            <span>ID: #{log.id}</span>
                            <span>•</span>
                            <span>{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                    </div>
                </div>
                <div className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-xl text-sm font-black uppercase tracking-widest">
                    {log.event_type.replace(/_/g, ' ')}
                </div>
            </div>

            <div className="w-full max-w-7xl p-6 lg:p-12 space-y-12">

                {/* 3 Stages Balance Breakdown */}
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-black text-white flex items-center gap-3">
                            <Activity size={24} className="text-blue-500" />
                            Financial Stages Overview
                        </h2>
                    </div>

                    {loadingRelated ? (
                        <div className="p-20 text-center text-white/20 animate-pulse bg-white/5 rounded-3xl border border-white/5">
                            Reconstructing financial thread...
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {relatedLogs.map((rl, idx) => {
                                const netDelta = rl.amount - rl.fee_amount;
                                const isPositive = netDelta > 0;
                                const isNegative = netDelta < 0;

                                return (
                                    <div key={rl.id} className="bg-black/40 border border-white/5 rounded-3xl overflow-hidden relative group">

                                        <div className="bg-white/5 p-4 flex items-center justify-between border-b border-white/5">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-600/20 border border-white/10 flex items-center justify-center font-black text-white">
                                                    {rl.username?.[0]?.toUpperCase() || 'S'}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-black text-white">@{rl.username || 'System User'}</div>
                                                    <div className="text-[10px] text-white/40 font-mono">TGID: {rl.telegram_id || rl.user_id} | Event: {rl.event_type}</div>
                                                </div>
                                            </div>
                                            <div className="px-4 py-1.5 bg-white/5 rounded-full text-[10px] font-black uppercase text-white/60 border border-white/5">
                                                Party {idx + 1}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/5">
                                            {/* Stage 1: Initial */}
                                            <div className="p-6 relative">
                                                <div className="absolute top-0 right-0 p-3 opacity-10"><Clock size={48} /></div>
                                                <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-6 bg-blue-500/10 inline-block px-3 py-1 rounded-lg">Stage 1: Before</div>
                                                <div className="space-y-4">
                                                    <div>
                                                        <div className="text-[9px] text-white/40 uppercase font-black tracking-wider mb-1">Available Balance</div>
                                                        <div className="text-2xl font-mono text-white">{formatGRAM(rl.balance_before)} <span className="text-[10px] text-white/20">GRAM</span></div>
                                                    </div>
                                                    <div>
                                                        <div className="text-[9px] text-white/40 uppercase font-black tracking-wider mb-1">Frozen Balance</div>
                                                        <div className="text-xl font-mono text-orange-400/80">{formatGRAM(rl.frozen_before)} <span className="text-[10px] text-orange-400/20">GRAM</span></div>
                                                    </div>
                                                </div>
                                                <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 bg-[#0d0d12] border border-white/10 rounded-full flex items-center justify-center text-white/40"><ChevronLeft size={12} className="rotate-180" /></div>
                                            </div>

                                            {/* Stage 2: Transaction */}
                                            <div className="p-6 relative bg-white/[0.02]">
                                                <div className="absolute top-0 right-0 p-3 opacity-10"><TrendingUp size={48} /></div>
                                                <div className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-6 bg-purple-500/10 inline-block px-3 py-1 rounded-lg">Stage 2: Transaction</div>
                                                <div className="space-y-4">
                                                    <div>
                                                        <div className="text-[9px] text-white/40 uppercase font-black tracking-wider mb-1">Gross Amount</div>
                                                        <div className={`text-2xl font-mono ${isPositive ? 'text-green-400' : (isNegative ? 'text-red-400' : 'text-white')}`}>
                                                            {isPositive ? '+' : ''}{formatGRAM(rl.amount)} <span className="text-[10px] text-white/20">GRAM</span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-[9px] text-white/40 uppercase font-black tracking-wider mb-1">System Fee</div>
                                                        <div className="text-xl font-mono text-red-400/80">-{formatGRAM(rl.fee_amount)} <span className="text-[10px] text-red-400/20">GRAM</span></div>
                                                    </div>
                                                </div>
                                                <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 bg-[#0d0d12] border border-white/10 rounded-full flex items-center justify-center text-white/40"><ChevronLeft size={12} className="rotate-180" /></div>
                                            </div>

                                            {/* Stage 3: Final */}
                                            <div className="p-6 relative">
                                                <div className="absolute top-0 right-0 p-3 opacity-10"><ShieldAlert size={48} /></div>
                                                <div className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-6 bg-emerald-500/10 inline-block px-3 py-1 rounded-lg">Stage 3: After</div>
                                                <div className="space-y-4">
                                                    <div>
                                                        <div className="text-[9px] text-white/40 uppercase font-black tracking-wider mb-1">Available Balance</div>
                                                        <div className={`text-2xl font-mono font-black ${rl.balance_after !== rl.balance_before ? 'text-white' : 'text-white/60'}`}>
                                                            {formatGRAM(rl.balance_after)} <span className="text-[10px] text-white/20">GRAM</span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-[9px] text-white/40 uppercase font-black tracking-wider mb-1">Frozen Balance</div>
                                                        <div className={`text-xl font-mono font-black ${rl.frozen_after !== rl.frozen_before ? 'text-orange-400' : 'text-orange-400/60'}`}>
                                                            {formatGRAM(rl.frozen_after)} <span className="text-[10px] text-orange-400/20">GRAM</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Metadata & Details Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Metadata Detail */}
                    <div>
                        <h3 className="text-xs font-black text-white uppercase tracking-widest mb-4 flex items-center gap-2">
                            <Database size={14} className="text-purple-500" /> Transactional Metadata
                        </h3>
                        <div className="bg-black/40 border border-white/5 rounded-3xl overflow-hidden shadow-xl">
                            <table className="w-full text-left text-[11px]">
                                <thead className="bg-white/5 text-white/40 font-black uppercase text-[9px]">
                                    <tr>
                                        <th className="px-6 py-4">Key</th>
                                        <th className="px-6 py-4">Value</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5 text-white/80">
                                    {Object.entries(metadata).map(([key, val]: [string, any]) => (
                                        <tr key={key} className="hover:bg-white/[0.02] transition-colors">
                                            <td className="px-6 py-4 font-black text-blue-400 capitalize">{key.replace(/_/g, ' ')}</td>
                                            <td className="px-6 py-4 font-mono">{String(val)}</td>
                                        </tr>
                                    ))}
                                    {Object.keys(metadata).length === 0 && (
                                        <tr>
                                            <td colSpan={2} className="px-6 py-8 text-center text-white/20 font-mono">No metadata associated</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Technical Payload */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xs font-black text-white uppercase tracking-widest flex items-center gap-2">
                                <Share2 size={14} className="text-orange-500" /> Raw Technical Trace
                            </h3>
                        </div>
                        <div className="bg-black/60 rounded-3xl p-6 font-mono text-[10px] leading-relaxed text-blue-300/80 border border-white/5 overflow-x-auto whitespace-pre-wrap shadow-inner relative h-full">
                            {JSON.stringify(log, null, 4)}
                        </div>
                    </div>
                </div>

                <div className="py-12 flex flex-col items-center opacity-30">
                    <ShieldAlert size={32} className="mb-4" />
                    <div className="text-[10px] font-black uppercase tracking-[0.3em]">End of Audit Report</div>
                </div>
            </div>
        </motion.div>
    );
};

const MonitoringLogs = () => {
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
    const [selectedLog, setSelectedLog] = useState<any>(null);

    const formatGRAM = (val: string | number) => {
        const num = typeof val === 'string' ? parseFloat(val) : val;
        if (isNaN(num)) return '0';
        return parseFloat(num.toFixed(9)).toString();
    };

    const fetchLogs = useCallback(async () => {
        try {
            const res = await adminApi.getMonitoringLogs();
            const data = await res.json();
            if (data.success) setLogs(data.logs);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const filteredLogs = logs.filter(l =>
        l.event_type.toLowerCase().includes(filter.toLowerCase()) ||
        l.username?.toLowerCase().includes(filter.toLowerCase()) ||
        String(l.user_id).includes(filter)
    );

    const getEventBadge = (type: string) => {
        const styles: Record<string, string> = {
            deposit: 'bg-green-500/10 text-green-400 border-green-500/20',
            withdraw: 'bg-red-500/10 text-red-400 border-red-500/20',
            p2p_lock: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
            p2p_settle_in: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
            p2p_settle_out: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
            store_buy: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
            nft_auto_settle: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
            p2p_cancel: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
        };
        return styles[type] || 'bg-white/5 text-white/40 border-white/10';
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-400 flex items-center gap-2">
                    <ShieldAlert size={10} /> Precision Audit Trail
                </h3>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search size={10} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/20" />
                        <input
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                            placeholder="Search Logs..."
                            className="bg-black/40 border border-white/5 rounded-lg pl-7 pr-3 py-1 text-[9px] text-white outline-none focus:border-blue-500/50 w-32"
                        />
                    </div>
                    <button onClick={fetchLogs} className="p-1.5 hover:bg-white/5 rounded-lg text-white/20 hover:text-white transition-all">
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                    </button>
                </div>
            </div>

            {loading ? <div className="p-4 bg-white/5 rounded-xl animate-pulse h-60" /> : (
                <div className="bg-black/40 rounded-2xl border border-white/5 overflow-hidden">
                    <div className="max-h-[600px] overflow-y-auto divide-y divide-white/5 custom-scrollbar">
                        {filteredLogs.map(log => {
                            const isExpanded = expandedLogId === log.id;
                            return (
                                <div
                                    key={log.id}
                                    className={`p-4 hover:bg-white/[0.04] transition-all group border-l-2 ${isExpanded ? 'bg-white/[0.04] border-blue-500' : 'border-transparent'}`}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className={`px-2 py-0.5 rounded text-[8px] font-black border uppercase tracking-wider ${getEventBadge(log.event_type)}`}>
                                                {log.event_type.replace(/_/g, ' ')}
                                            </div>
                                            <div className="flex flex-col">
                                                <div className="text-[11px] font-bold text-white flex items-center gap-1.5 leading-none">
                                                    @{log.username || 'sys'}
                                                    <span className="text-[9px] px-1.5 py-0.5 bg-white/5 rounded text-white/40 font-mono">ID: {log.telegram_id || log.user_id}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="text-[9px] text-white/20 font-mono">
                                                {new Date(log.created_at).toLocaleString()}
                                            </div>
                                            <button
                                                onClick={() => setSelectedLog(log)}
                                                className="p-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg transition-all opacity-0 group-hover:opacity-100 flex items-center gap-1.5"
                                            >
                                                <span className="text-[8px] font-black uppercase tracking-tighter">Deep Audit</span>
                                                <ExternalLink size={10} />
                                            </button>
                                            <button
                                                onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                                                className="p-1.5 hover:bg-white/5 rounded-lg text-white/20 transition-all"
                                            >
                                                {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div
                                        onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                                        className="grid grid-cols-3 gap-2 cursor-pointer"
                                    >
                                        <div className="bg-white/5 rounded-lg p-2 border border-white/5">
                                            <div className="text-[7px] text-white/40 uppercase font-black mb-1">Transaction</div>
                                            <div className="flex items-baseline gap-1 overflow-hidden">
                                                <span className="text-sm font-black text-white truncate">{formatGRAM(log.amount)}</span>
                                                <span className="text-[8px] text-white/20 shrink-0">GRAM</span>
                                            </div>
                                            {log.fee_amount > 0 && (
                                                <div className="text-[8px] text-red-400/60 font-bold mt-0.5">-{formatGRAM(log.fee_amount)} Fee</div>
                                            )}
                                        </div>

                                        <div className="bg-white/5 rounded-lg p-2 border border-white/5">
                                            <div className="text-[7px] text-white/40 uppercase font-black mb-1">Available Balance</div>
                                            <div className="flex items-center gap-1.5 overflow-hidden">
                                                <span className="text-[9px] font-mono text-white/40 truncate">{formatGRAM(log.balance_before)}</span>
                                                <ArrowRightLeft size={8} className="text-blue-500/50 shrink-0" />
                                                <span className="text-[9px] font-mono font-bold text-blue-400 truncate">{formatGRAM(log.balance_after)}</span>
                                            </div>
                                        </div>

                                        <div className="bg-white/5 rounded-lg p-2 border border-white/5">
                                            <div className="text-[7px] text-white/40 uppercase font-black mb-1">Frozen Balance</div>
                                            <div className="flex items-center gap-1.5 overflow-hidden">
                                                <span className="text-[9px] font-mono text-white/40 truncate">{formatGRAM(log.frozen_before)}</span>
                                                <ArrowRightLeft size={8} className="text-orange-500/50 shrink-0" />
                                                <span className="text-[9px] font-mono font-bold text-orange-400 truncate">{formatGRAM(log.frozen_after)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <AnimatePresence>
                                        {isExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="overflow-hidden"
                                            >
                                                <div className="pt-4 mt-3 border-t border-white/5 space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="text-[10px] font-black uppercase text-white/20 tracking-widest">Metadata Context</div>
                                                        <button
                                                            onClick={() => setSelectedLog(log)}
                                                            className="text-[9px] font-black text-blue-500 hover:underline flex items-center gap-1"
                                                        >
                                                            Open Full Inspection <ArrowUpRight size={10} />
                                                        </button>
                                                    </div>
                                                    <div className="bg-black/60 rounded-xl p-3 border border-white/5">
                                                        <pre className="text-[9px] text-blue-300 font-mono overflow-x-auto whitespace-pre-wrap">
                                                            {JSON.stringify(typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata, null, 2)}
                                                        </pre>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                        {filteredLogs.length === 0 && (
                            <div className="p-12 text-center text-white/20 text-xs italic">No matching audit logs found.</div>
                        )}
                    </div>
                </div>
            )}

            <AnimatePresence>
                {selectedLog && (
                    <FullAuditView
                        log={selectedLog}
                        onClose={() => setSelectedLog(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

const ProfitDashboard = () => {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await adminApi.getProfitStats();
            const data = await res.json();
            if (data.success) setStats(data.stats);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const ProfitCard = ({ title, value, icon: Icon, color, subtext }: any) => (
        <div className="bg-[#1c1c1e] p-5 rounded-2xl border border-white/5 shadow-xl relative overflow-hidden group">
            <div className={`absolute -right-4 -top-4 w-16 h-16 opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-all`} style={{ backgroundColor: color }} />
            <div className="flex items-center gap-2 text-white/40 text-[10px] font-black uppercase tracking-[0.1em] mb-2">
                <Icon size={12} style={{ color }} /> {title}
            </div>
            <div className="flex items-baseline gap-1">
                <h3 className="text-2xl font-black text-white font-mono">{Number(value).toLocaleString()}</h3>
                <span className="text-[10px] font-bold text-white/20">GRAM</span>
            </div>
            {subtext && <div className="mt-2 text-[9px] font-bold uppercase tracking-tighter" style={{ color: `${color}cc` }}>{subtext}</div>}
        </div>
    );

    if (loading) return <div className="p-12 text-center text-white/20 animate-pulse">Calculating Profits...</div>;

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-black text-white uppercase tracking-widest flex items-center gap-3">
                    <PieChart size={20} className="text-blue-500" /> Profit Distribution
                </h2>
                <button onClick={fetchStats} className="p-2 bg-white/5 rounded-lg text-white/40 hover:text-white transition-all">
                    <RefreshCw size={14} />
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <ProfitCard title="P2P Commissions" value={stats?.p2p} icon={Activity} color="#3390EC" subtext="1.5% Fee Volume" />
                <ProfitCard title="Market & Auctions" value={stats?.market} icon={DollarSign} color="#10B981" subtext="Direct Sales" />
                <ProfitCard title="NFT Fees" value={stats?.nft} icon={Package} color="#F59E0B" subtext="Withdrawals & Sales" />
                <ProfitCard title="Cover Sales" value={stats?.skins} icon={ImageIcon} color="#8B5CF6" subtext="Skin Purchases" />
                <ProfitCard title="Brand Sales" value={stats?.brands} icon={Package} color="#EC4899" subtext="Package Sales" />
                <ProfitCard title="Transfer Fees" value={stats?.transfers} icon={ArrowUpRight} color="#6366F1" subtext="P2P Transfers" />
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 p-6 rounded-2xl flex items-center justify-between">
                <div>
                    <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Total System Revenue</h4>
                    <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-black text-white">{Number(stats?.totalFees).toLocaleString()}</span>
                        <span className="text-sm font-bold text-blue-500">GRAM</span>
                    </div>
                </div>
                <div className="p-4 bg-blue-500/20 rounded-2xl text-blue-400">
                    <TrendingUp size={32} />
                </div>
            </div>
        </div>
    );
};

const DisputeManager = () => {
    const [disputes, setDisputes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const { showNotification } = useNotify();
    const { notificationOccurred } = useHapticFeedback();
    const [resolveDisputeData, setResolveDisputeData] = useState<{dealId: number, resolution: 'refund' | 'release'} | null>(null);

    const fetchDisputes = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminApi.getDisputes();
            const data = await res.json();
            if (data.success) setDisputes(data.disputes);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDisputes(); }, [fetchDisputes]);

    const handleResolveClick = (dealId: number, resolution: 'refund' | 'release') => {
        setResolveDisputeData({ dealId, resolution });
    };

    const executeResolve = async (reason: string) => {
        if (!resolveDisputeData || !reason) {
            setResolveDisputeData(null);
            return;
        }
        
        const { dealId, resolution } = resolveDisputeData;
        setResolveDisputeData(null);

        try {
            const res = await adminApi.resolveDispute({ dealId, resolution, adminReason: reason });
            const data = await res.json();
            if (data.success) {
                notificationOccurred('success');
                showNotification('Resolved', `Dispute ${dealId} resolved: ${resolution}`, 'success');
                fetchDisputes();
            } else {
                showNotification('Error', data.error, 'error');
            }
        } catch (e) {
            showNotification('Error', 'Failed to connect', 'error');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-white/40 uppercase text-xs font-bold tracking-wider flex items-center gap-2">
                    <ShieldAlert size={12} className="text-red-400" /> P2P Dispute Management
                </h2>
                <button onClick={fetchDisputes} className="p-2 bg-white/5 rounded-lg text-white/40 hover:text-white transition-all">
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>

            {loading ? <div className="p-8 text-center text-white/20">Loading disputed deals...</div> : (
                <div className="grid gap-4">
                    {disputes.map(d => (
                        <div key={d.deal_id} className="bg-[#1c1c1e] border border-white/5 rounded-2xl p-5 space-y-4">
                            <div className="flex items-center justify-between border-b border-white/5 pb-3">
                                <div>
                                    <div className="text-xs font-bold text-white">Deal #{d.deal_id}</div>
                                    <div className="text-[10px] text-white/40 font-mono">{new Date(d.created_at).toLocaleString()}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-black text-[#3390EC] font-mono">{d.price} <span className="text-[10px]">GRAM</span></div>
                                    <div className="text-[9px] text-red-400 font-bold uppercase py-0.5 px-1.5 bg-red-400/10 rounded">DISPUTED</div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white/5 p-3 rounded-xl">
                                    <div className="text-[10px] text-white/30 uppercase font-black mb-1">Buyer</div>
                                    <div className="text-sm font-bold">@{d.buyer_username}</div>
                                    <div className="text-[10px] text-white/20 font-mono">TG: {d.buyer_tg}</div>
                                </div>
                                <div className="bg-white/5 p-3 rounded-xl">
                                    <div className="text-[10px] text-white/30 uppercase font-black mb-1">Seller</div>
                                    <div className="text-sm font-bold">@{d.seller_username}</div>
                                    <div className="text-[10px] text-white/20 font-mono">TG: {d.seller_tg}</div>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => handleResolveClick(d.deal_id, 'refund')}
                                    className="flex-1 py-3 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-xl text-xs font-black uppercase tracking-widest border border-red-500/20 transition-all"
                                >
                                    Refund Buyer
                                </button>
                                <button
                                    onClick={() => handleResolveClick(d.deal_id, 'release')}
                                    className="flex-1 py-3 bg-green-500/10 hover:bg-green-500 text-green-500 hover:text-white rounded-xl text-xs font-black uppercase tracking-widest border border-green-500/20 transition-all"
                                >
                                    Release to Seller
                                </button>
                            </div>
                        </div>
                    ))}
                    {disputes.length === 0 && (
                        <div className="p-12 text-center border border-dashed border-white/5 rounded-3xl text-white/20">
                            <ShieldAlert size={48} className="mx-auto mb-4 opacity-10" />
                            <p className="text-sm font-bold uppercase tracking-widest">No active disputes</p>
                            <p className="text-xs mt-1">System is healthy. All P2P deals are proceeding normally.</p>
                        </div>
                    )}
                </div>
            )}

            <PromptModal
                isOpen={resolveDisputeData !== null}
                onClose={() => setResolveDisputeData(null)}
                onSubmit={executeResolve}
                title={`Resolve Dispute (${resolveDisputeData?.resolution === 'refund' ? 'Refund Buyer' : 'Pay Seller'})`}
                message={`Please provide a reason for the ${resolveDisputeData?.resolution}.`}
                placeholder="Reason..."
                defaultValue="Admin Resolution"
                confirmText="Submit Resolution"
                inputType="text"
            />
        </div>
    );
};

const AdminDashboard = ({ onBack }: { onBack: () => void }) => {
    const { impactOccurred, notificationOccurred } = useHapticFeedback();
    const { showNotification } = useNotify();

    // Reorganized Navigation State
    type Category = 'analytics' | 'factory' | 'ops' | 'audit' | 'system' | 'blueprints' | 'controller_cat' | 'gifting_cat' | 'broadcast_cat';
    type AdminMode = 'overview' | 'profits' | 'records' | 'subscribers' | 'monitor' | 'balance_audit' | 'new_package' | 'manage' | 'skins' | 'wallet_skins' | 'users' | 'levels' | 'ad_manager' | 'database' | 'bot_wallet' | 'funds' | 'in_package' | 'system_map' | 'disputes' | 'ui_config' | 'gifting' | 'infousers' | 'controller' | 'global_broadcast';

    const [activeCategory, setActiveCategory] = useState<Category>('analytics');
    const [uploadMode, setUploadMode] = useState<AdminMode>('overview');
    
    // Modals state
    const [deleteCardConfirm, setDeleteCardConfirm] = useState<string | null>(null);
    const [deletePackageConfirm, setDeletePackageConfirm] = useState<string | null>(null);
    const [truncateTableConfirm, setTruncateTableConfirm] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const CATEGORIES = useMemo(() => [
        { id: 'analytics' as const, label: 'Analytics', icon: BarChart3, color: '#3390EC', modes: ['overview', 'profits', 'records'] },
        { id: 'audit' as const, label: 'Audit', icon: ShieldAlert, color: '#EC4899', modes: ['monitor', 'balance_audit', 'disputes', 'infousers'] },
        { id: 'factory' as const, label: 'Factory', icon: Package, color: '#F59E0B', modes: ['new_package', 'manage', 'skins', 'wallet_skins', 'in_package'] },
        { id: 'ops' as const, label: 'Hub', icon: Users, color: '#10B981', modes: ['users', 'subscribers', 'levels', 'ad_manager'] },
        { id: 'system' as const, label: 'Core', icon: Database, color: '#6366F1', modes: ['database', 'bot_wallet', 'funds', 'ui_config'] },
        { id: 'blueprints' as const, label: 'Blueprint', icon: Share2, color: '#A855F7', modes: ['system_map'] },
        { id: 'controller_cat' as const, label: 'Controller', icon: Settings, color: '#EF4444', modes: ['controller'] },
        { id: 'gifting_cat' as const, label: 'Gift Center', icon: Gift, color: '#10B981', modes: ['gifting'] },
        { id: 'broadcast_cat' as const, label: 'Broadcast', icon: Package, color: '#F97316', modes: ['global_broadcast'] }
    ], []);

    const getModeLabel = (mode: AdminMode) => {
        const labels: Record<string, string> = {
            overview: 'Dashboard', profits: 'Revenue',
            monitor: 'App Monitor', balance_audit: 'Precise Audit',
            new_package: 'Create Pack', manage: 'Storage', skins: 'Profile Covers', wallet_skins: 'Wallet Cards', users: 'User List',
            levels: 'Subs', ad_manager: 'Ad Hub', database: 'Database',
            bot_wallet: 'Fees', funds: 'Recovery', system_map: 'Architecture',
            disputes: 'Disputes', records: 'Global Records', subscribers: 'Subscribers',
            ui_config: 'UI Settings', gifting: 'Individual Gift', infousers: 'InfoUsers', controller: 'Controller', global_broadcast: 'Mass Distribute'
        };
        return labels[mode] || mode.toUpperCase();
    };

    const getModeIcon = (mode: AdminMode) => {
        const icons: Record<string, any> = {
            overview: Activity, profits: DollarSign,
            monitor: Eye, balance_audit: ShieldAlert,
            new_package: Plus, manage: LayoutGrid, skins: ImageIcon, users: Users,
            levels: TrendingUp, ad_manager: Send, database: Database,
            bot_wallet: Wallet, funds: ShieldAlert,
            disputes: Gavel, records: Hash, subscribers: PieChart,
            ui_config: Layout, gifting: Gift, infousers: Users, controller: Settings, global_broadcast: Package
        };
        return icons[mode] || Settings;
    };
    const [hasUploadedCards, setHasUploadedCards] = useState(false);

    // Stats
    const [systemStats, setSystemStats] = useState<any>(null);

    const fetchSystemStats = () => {
        adminApi.getSystemStats()
            .then(res => res.json())
            .then(setSystemStats)
            .catch(console.error);
    };

    // Existing Packages for "Add to Existing" mode
    const [existingPackages, setExistingPackages] = useState<any[]>([]);
    const [selectedPackageId, setSelectedPackageId] = useState<string>('');

    const [coverImage, setCoverImage] = useState<string | null>(null);
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [packageName, setPackageName] = useState('');
    const [characterName, setCharacterName] = useState('');
    const [totalSupply, setTotalSupply] = useState(25000);
    const [isFree, setIsFree] = useState(false);
    const [baseColor, setBaseColor] = useState('#3390EC');
    const [baseTextColor, setBaseTextColor] = useState('#FFFFFF');
    const [isNewUserGift, setIsNewUserGift] = useState(false);

    // Cards in Package
    const [cards, setCards] = useState<CardDefinition[]>([]);

    // Manage Mode State
    const [expandedPackageId, setExpandedPackageId] = useState<string | null>(null);
    const [packageCards, setPackageCards] = useState<any[]>([]);
    const [loadingCards, setLoadingCards] = useState(false);

    // Users Mode State
    const [usersList, setUsersList] = useState<any[]>([]);

    // Database Mode State
    const [dbStats, setDbStats] = useState<{ tables: any[], totalSizeMB: string } | null>(null);

    const fetchDbStats = () => {
        adminApi.getDatabaseStats()
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setDbStats(data);
                }
            })
            .catch(console.error);
    };

    const fetchUsers = () => {
        adminApi.getAllUsers()
            .then(res => res.json())
            .then(setUsersList)
            .catch(console.error);
    };

    const handleModeChange = (mode: AdminMode) => {
        setUploadMode(mode);
        impactOccurred('light');

        if (mode === 'manage') fetchPackages();
        if (mode === 'users') fetchUsers();
        if (mode === 'database') fetchDbStats();

        // Find category for this mode and set it
        const cat = CATEGORIES.find(c => c.modes.includes(mode as any));
        if (cat) setActiveCategory(cat.id);
    };

    // Initial Fetch
    useEffect(() => {
        fetchSystemStats();
    }, []);

    const fetchPackages = () => {
        adminApi.getPackages()
            .then(res => res.json())
            .then(setExistingPackages)
            .catch(console.error);
    };

    const handleExpandPackage = async (pkgId: string) => {
        if (expandedPackageId === pkgId) {
            setExpandedPackageId(null);
            return;
        }
        setExpandedPackageId(pkgId);
        setLoadingCards(true);
        try {
            const res = await adminApi.getPackageCards(Number(pkgId));
            const data = await res.json();
            setPackageCards(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingCards(false);
        }
    };

    const handleDeleteCardClick = (cardId: string) => {
        setDeleteCardConfirm(cardId);
    };

    const executeDeleteCard = async () => {
        if (!deleteCardConfirm) return;
        setIsDeleting(true);
        try {
            const res = await adminApi.deleteCard(Number(deleteCardConfirm));
            const data = await res.json();
            if (res.ok && data.success) {
                notificationOccurred('success');
                setPackageCards(prev => prev.filter(c => String(c.id) !== deleteCardConfirm));
            } else {
                showNotification('Delete Failed', data.error || "Failed to delete card.", 'error');
            }
        } catch (e) {
            showNotification('Error', 'Network Error: Could not connect to server.', 'error');
        } finally {
            setIsDeleting(false);
            setDeleteCardConfirm(null);
        }
    };

    const handleDeletePackageClick = (pkgId: string) => {
        setDeletePackageConfirm(pkgId);
    };

    const executeDeletePackage = async () => {
        if (!deletePackageConfirm) return;
        setIsDeleting(true);
        try {
            const res = await adminApi.deletePackage(Number(deletePackageConfirm));
            const data = await res.json();
            if (res.ok && data.success) {
                notificationOccurred('success');
                setExistingPackages(prev => prev.filter(p => String(p.id) !== deletePackageConfirm));
            } else {
                showNotification('Delete Failed', data.error || "Failed to delete package.", 'error');
            }
        } catch (e) {
            showNotification('Error', 'Network Error: Could not connect to server.', 'error');
        } finally {
            setIsDeleting(false);
            setDeletePackageConfirm(null);
        }
    };

    const executeTruncateTable = async () => {
        if (!truncateTableConfirm) return;
        setIsDeleting(true);
        try {
            const res = await adminApi.truncateTable(truncateTableConfirm);
            const data = await res.json();
            if (data.success) {
                notificationOccurred('success');
                showNotification('Success', 'Table truncated successfully.', 'success');
                fetchDbStats(); // Refresh
            } else {
                showNotification('Error', "Failed: " + data.error, 'error');
            }
        } catch (e) {
            showNotification('Error', 'Error truncating table', 'error');
            console.error(e);
        } finally {
            setIsDeleting(false);
            setTruncateTableConfirm(null);
        }
    };

    // Fetch existing packages on mount
    useEffect(() => {
        adminApi.getPackages()
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setExistingPackages(data);
                }
            })
            .catch(err => console.error("Failed to fetch packages:", err));
    }, []);

    // Handler for Cover Image (Only for New Package)
    const handleCoverUpload = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setCoverFile(file);
            setCoverImage(URL.createObjectURL(file));
            impactOccurred('light');
        }
    };

    const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const newCards: CardDefinition[] = Array.from(e.target.files).map(file => ({
                id: Math.random().toString(36).substr(2, 9),
                file: file,
                previewUrl: URL.createObjectURL(file), // Using local preview for now
                name: file.name.split('.')[0],
                rarity: 50,
                rank: 'Bronze',
                level: 1,
                color: baseColor,
                textColor: baseTextColor,
                quantity: Math.floor(totalSupply / (e.target.files?.length || 1)),
                isFree: isFree,
                price: 0,
                listingType: 'Market Only',
                royaltyFee: 0,
                isTradable: true,
                accessLevel: 1,
                dropRate: 0,
                iconColor: '#FFFFFF',
                specialFx: 'None',
                variantGroupId: '',
                skinLabel: ''
            }));
            setCards(prev => [...prev, ...newCards]);
            setHasUploadedCards(true); // Unlock inputs
            impactOccurred('light');
        }
    };

    const updateCard = (id: string, field: keyof CardDefinition, value: any) => {
        setCards(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
    };

    const removeCard = (id: string) => {
        const newCards = cards.filter(c => c.id !== id);
        setCards(newCards);
        if (newCards.length === 0) setHasUploadedCards(false); // Lock if empty
        notificationOccurred('warning');
    };

    const handleSave = async () => {
        if (isSaving) return;
        if (!hasUploadedCards) {
            notificationOccurred('error');
            showNotification('Error', 'Please upload at least one card.', 'error');
            return;
        }

        const formData = new FormData();
        formData.append('mode', uploadMode);

        // Prepare Package Metadata
        const packagePayload: any = {
            name: packageName,
            character: characterName,
            supply: totalSupply,
            isFree,
            color: baseColor,
            textColor: baseTextColor,
            isNewUserGift
        };

        if (uploadMode === 'new_package') {
            packagePayload.cover = coverFile?.name;
        } else {
            packagePayload.id = selectedPackageId; // Send ID for existing
        }

        formData.append('package', JSON.stringify(packagePayload));

        // Append Card Metadata
        formData.append('cards', JSON.stringify(cards.map(c => ({
            originalName: c.file?.name,
            name: c.name,
            rarity: c.rarity,
            rank: c.rank,
            color: c.color,
            textColor: c.textColor,
            quantity: c.quantity,
            isFree: c.isFree,
            price: c.price,
            listingType: c.listingType,
            royaltyFee: c.royaltyFee,
            isTradable: c.isTradable,
            accessLevel: c.accessLevel,
            dropRate: c.dropRate,
            iconColor: c.iconColor,
            specialFx: c.specialFx,
            variantGroupId: c.variantGroupId,
            skinLabel: c.skinLabel
        }))));

        // Append File Assets
        if (coverFile) {
            formData.append('cover', coverFile);
        }

        cards.forEach(c => {
            if (c.file) {
                formData.append('cards', c.file);
            }
        });

        setIsSaving(true);
        try {
            const response = await adminApi.createPackage(formData);

            const text = await response.text();
            console.log("Details:", response.status, text);

            if (!response.ok) {
                throw new Error(`Server Error (${response.status}): ${text.substring(0, 100)}`);
            }

            let result;
            try {
                result = JSON.parse(text);
            } catch (e) {
                throw new Error("Invalid JSON: " + text.substring(0, 50));
            }

            if (result.success) {
                console.log("Saved Successfully:", result);
                notificationOccurred('success');
                impactOccurred('heavy');
                showNotification('Success', "Package Uploaded Successfully! ID: " + result.packageId, 'success');

                // Reset
                setCards([]);
                setHasUploadedCards(false);
                setPackageName('');
                setCoverImage(null);
                setCoverFile(null);
                // Refresh list
                adminApi.getPackages()
                    .then(res => res.json())
                    .then(data => setExistingPackages(data));
            } else {
                throw new Error(result.error || "Unknown error");
            }
        } catch (error: any) {
            console.error("Upload Failed:", error);
            notificationOccurred('error');
            showNotification('Upload Failed', error.message, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-black text-white pb-32">
            {/* Header */}
            <div className="sticky top-0 z-50 bg-[#1c1c1e]/80 backdrop-blur-md border-b border-white/5 px-4 h-14 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={onBack} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10">
                        <ChevronLeft size={20} />
                    </button>
                    <h1 className="font-bold text-lg">Admin / Developer</h1>
                </div>
                <button
                    disabled={!hasUploadedCards || isSaving}
                    onClick={handleSave}
                    className={`px-4 py-1.5 rounded-full text-sm font-bold flex items-center gap-2 transition-all ${hasUploadedCards && !isSaving ? 'bg-[#3390EC] active:scale-95' : 'bg-white/10 text-white/20 cursor-not-allowed'}`}
                >
                    {isSaving ? (
                        <RefreshCw size={16} className="animate-spin" />
                    ) : (
                        <Save size={16} />
                    )}
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
            </div>

            <div className="p-4 space-y-8">

                {/* Navigation System */}
                <div className="px-4 pt-4 space-y-4">
                    {/* Categories */}
                    <div className="flex items-center gap-2">
                        {CATEGORIES.map(cat => {
                            const Icon = cat.icon;
                            const isActive = activeCategory === cat.id;
                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => {
                                        setActiveCategory(cat.id);
                                        impactOccurred('light');
                                        // If switching category, pick the first mode of that category
                                        const firstMode = cat.modes[0] as AdminMode;
                                        handleModeChange(firstMode);
                                    }}
                                    className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-2xl transition-all relative overflow-hidden ${isActive ? 'bg-white/10 shadow-lg' : 'bg-white/5 opacity-40 hover:opacity-100'}`}
                                >
                                    <Icon size={20} style={{ color: isActive ? cat.color : undefined }} />
                                    <span className="text-[10px] font-black uppercase tracking-wider">{cat.label}</span>
                                    {isActive && (
                                        <motion.div
                                            layoutId="active-cat"
                                            className="absolute bottom-0 left-0 right-0 h-1"
                                            style={{ backgroundColor: cat.color }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Sub-modes for active category */}
                    <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1">
                        {CATEGORIES.find(c => c.id === activeCategory)?.modes.map(mode => {
                            if (mode === 'in_package') return null; // Hidden sub-mode
                            const isActive = uploadMode === mode;
                            const ModeIcon = getModeIcon(mode as AdminMode);
                            return (
                                <button
                                    key={mode}
                                    onClick={() => handleModeChange(mode as AdminMode)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${isActive ? 'bg-[#3390EC] text-white' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                                >
                                    <ModeIcon size={14} />
                                    {getModeLabel(mode as AdminMode)}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="p-4">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={uploadMode}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="space-y-8"
                        >

                            {/* AD MANAGER MODE */}
                            {uploadMode === 'ad_manager' && (
                                <AdManager />
                            )}

                            {/* SYSTEM MAP MODE */}
                            {uploadMode === 'system_map' && (
                                <SystemMap />
                            )}

                            {/* BOT WALLET MODE */}
                            {uploadMode === 'bot_wallet' && (
                                <BotWalletPanel />
                            )}

                            {/* GLOBAL BROADCAST MODE */}
                            {uploadMode === 'global_broadcast' && (
                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <GlobalBroadcastManager />
                                </div>
                            )}

                            {/* LEVELS MANAGER MODE */}
                            {uploadMode === 'levels' && (
                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <LevelManager />
                                </div>
                            )}


                            {/* PROFITS DASHBOARD MODE */}
                            {uploadMode === 'profits' && (
                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <ProfitDashboard />
                                </div>
                            )}

                            {/* RECORDS MODE */}
                            {uploadMode === 'records' && (
                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <RecordsManager />
                                </div>
                            )}

                            {/* SUBSCRIBERS MODE */}
                            {uploadMode === 'subscribers' && (
                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <SubscribersManager />
                                </div>
                            )}

                            {/* INFOUSERS TRACE */}
                            {uploadMode === 'infousers' && (
                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <InfoUsersPanel />
                                </div>
                            )}

                            {/* AUDIT & MONITORING MODES */}
                            {(uploadMode === 'monitor' || uploadMode === 'balance_audit') && (
                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <MonitoringLogs />
                                </div>
                            )}

                            {/* FUNDS MANAGER MODE */}
                            {uploadMode === 'funds' && (
                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <FundsManager />
                                </div>
                            )}

                            {/* DISPUTE MANAGER MODE */}
                            {uploadMode === 'disputes' && (
                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <DisputeManager />
                                </div>
                            )}

                            {/* COMMAND CENTER OVERVIEW */}
                            {uploadMode === 'overview' && systemStats && !systemStats.error && (
                                <div className="space-y-8 animate-in fade-in duration-700">
                                    {/* Summary Cards */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {[
                                            { label: 'Total Users', value: systemStats.users, icon: Users, color: '#3390EC', sub: 'Active Platform', subIcon: ArrowUpRight },
                                            { label: 'Total Volume', value: Number(systemStats.volume || 0).toLocaleString(), icon: DollarSign, color: '#F59E0B', sub: 'GRAM CURRENCY', subIcon: null },
                                            { label: 'Live Auctions', value: systemStats.activeAuctions, icon: Gavel, color: '#10B981', sub: 'Active Markets', subIcon: ArrowUpRight },
                                            { label: 'Platform Revenue', value: Number(systemStats.revenue || 0).toLocaleString(), icon: TrendingUp, color: '#EC4899', sub: 'FEE COLLECTIONS', subIcon: null }
                                        ].map((card, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: i * 0.1 }}
                                                className="bg-white/5 p-5 rounded-3xl border border-white/5 shadow-2xl relative overflow-hidden group hover:bg-white/10 transition-colors"
                                            >
                                                <div className="absolute -right-4 -top-4 w-16 h-16 opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-all" style={{ backgroundColor: card.color }} />
                                                <div className="flex items-center gap-2 text-white/40 text-[10px] font-black uppercase tracking-wider mb-2">
                                                    <card.icon size={12} style={{ color: card.color }} /> {card.label}
                                                </div>
                                                <h3 className="text-3xl font-black text-white font-mono tracking-tighter">{card.value}</h3>
                                                <div className="mt-2 text-[9px] font-bold flex items-center gap-1 uppercase tracking-tighter" style={{ color: `${card.color}cc` }}>
                                                    {card.sub} {card.subIcon && <card.subIcon size={10} />}
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>

                                    {/* Real-Time Monitoring Grid */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-4">
                                        <AuctionMonitor />
                                        <FinancialAudit />
                                    </div>
                                </div>
                            )}
                            {/* Error State Display */}
                            {uploadMode === 'overview' && systemStats && systemStats.error && (
                                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                                    Error fetching stats: {systemStats.error}
                                </div>
                            )}

                            {/* USERS MODE */}
                            {uploadMode === 'users' && (
                                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-white/40 uppercase text-xs font-bold tracking-wider flex items-center gap-2">
                                            User Management ({usersList.length})
                                        </h2>
                                    </div>
                                    <div className="bg-[#1c1c1e] border border-white/5 rounded-2xl overflow-hidden shadow-xl overflow-x-auto">
                                        <table className="w-full text-left text-[10px] font-mono whitespace-nowrap">
                                            <thead className="bg-white/5 text-white/40 border-b border-white/5">
                                                <tr>
                                                    <th className="p-3 font-bold uppercase">User ID</th>
                                                    <th className="p-3 font-bold uppercase">Telegram ID</th>
                                                    <th className="p-3 font-bold uppercase">Username</th>
                                                    <th className="p-3 font-bold uppercase">Name</th>
                                                    <th className="p-3 font-bold uppercase text-right">Balance</th>
                                                    <th className="p-3 font-bold uppercase text-right">Joined</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-white/5">
                                                {usersList.map((user, i) => (
                                                    <tr key={i} className="hover:bg-white/5">
                                                        <td className="p-3 text-white/60">{user.user_id}</td>
                                                        <td className="p-3 text-blue-400 font-bold">{user.telegram_id}</td>
                                                        <td className="p-3 text-white">@{user.username || 'N/A'}</td>
                                                        <td className="p-3 text-white/60">{user.first_name} {user.last_name}</td>
                                                        <td className="p-3 text-right text-green-400 font-bold">{(user.balance || 0).toLocaleString()}</td>
                                                        <td className="p-3 text-right text-white/40">{new Date(user.created_at || Date.now()).toLocaleDateString()}</td>
                                                    </tr>
                                                ))}
                                                {usersList.length === 0 && (
                                                    <tr>
                                                        <td colSpan={6} className="p-8 text-center text-white/40">No users found.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* DATABASE MODE */}
                            {uploadMode === 'database' && (
                                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-white/40 uppercase text-xs font-bold tracking-wider flex items-center gap-2">
                                            Database Storage Analysis
                                        </h2>
                                        <div className="bg-[#3390EC]/10 text-[#3390EC] px-4 py-2 rounded-xl text-sm font-bold shadow-lg shadow-[#3390EC]/10 border border-[#3390EC]/20">
                                            Total Size: {dbStats?.totalSizeMB || '0.00'} MB
                                        </div>
                                    </div>

                                    <div className="bg-[#1c1c1e] border border-white/5 rounded-2xl overflow-hidden shadow-xl">
                                        <div className="grid grid-cols-12 gap-4 p-4 bg-white/5 text-[10px] font-bold text-white/40 uppercase tracking-widest border-b border-white/5">
                                            <div className="col-span-6">Table Name</div>
                                            <div className="col-span-3 text-right">Rows</div>
                                            <div className="col-span-3 text-right">Size (MB)</div>
                                        </div>
                                        <div className="divide-y divide-white/5">
                                            {dbStats?.tables.map((table, i) => (
                                                <div key={i} className="group">
                                                    <div
                                                        onClick={() => setExpandedPackageId(expandedPackageId === table.tableName ? null : table.tableName)}
                                                        className="grid grid-cols-12 gap-4 p-4 hover:bg-white/5 transition-colors items-center cursor-pointer"
                                                    >
                                                        <div className="col-span-6 font-mono text-sm text-[#3390EC] font-bold flex items-center gap-2">
                                                            <ChevronLeft className={`transition-transform text-white/20 ${expandedPackageId === table.tableName ? '-rotate-90' : 'rotate-180'}`} size={12} />
                                                            {table.tableName}
                                                        </div>
                                                        <div className="col-span-3 text-right font-mono text-xs text-white/60">{Number(table.tableRows).toLocaleString()}</div>
                                                        <div className="col-span-3 text-right font-mono text-xs text-white">
                                                            <span className={`${Number(table.sizeMB) > 10 ? 'text-red-400 font-bold' : ''}`}>
                                                                {table.sizeMB} MB
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* EXPANDED DETAILS */}
                                                    {expandedPackageId === table.tableName && (
                                                        <div className="bg-black/20 p-4 border-t border-white/5 animate-in slide-in-from-top-2 duration-200 space-y-4">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-6 text-xs text-white/40 font-mono">
                                                                    <div>
                                                                        <span className="block text-[10px] uppercase opacity-50 mb-0.5">Data Size</span>
                                                                        <span className="text-white">{table.dataSizeMB || '0.00'} MB</span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="block text-[10px] uppercase opacity-50 mb-0.5">Index Size</span>
                                                                        <span className="text-white">{table.indexSizeMB || '0.00'} MB</span>
                                                                    </div>
                                                                </div>

                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setTruncateTableConfirm(table.tableName);
                                                                    }}
                                                                    className="px-4 py-2 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-lg text-xs font-bold transition-all flex items-center gap-2 border border-red-500/20"
                                                                >
                                                                    <Trash2 size={14} /> ZERO TABLE
                                                                </button>
                                                            </div>

                                                            {/* DATA VIEWER */}
                                                            <div className="bg-[#1c1c1e] rounded-xl border border-white/5 overflow-hidden">
                                                                <TableViewer tableName={table.tableName} />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            {!dbStats && <div className="p-8 text-center text-white/40 text-xs animate-pulse">Scanning database...</div>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* MANAGE MODE */}
                            {uploadMode === 'manage' && (
                                <div className="space-y-4">
                                    <h2 className="text-white/40 uppercase text-xs font-bold tracking-wider">Your Packages</h2>
                                    {existingPackages.map(pkg => (
                                        <div key={pkg.id} className="bg-[#1c1c1e] border border-white/5 rounded-2xl overflow-hidden">
                                            <div
                                                className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                                                onClick={() => handleExpandPackage(pkg.id)}
                                            >
                                                <div className="flex-1">
                                                    <h3 className="font-bold text-sm">{pkg.name}</h3>
                                                    <p className="text-xs text-white/40">{pkg.default_color} • Supply: {pkg.total_supply}</p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeletePackageClick(pkg.id);
                                                        }}
                                                        className="px-3 py-1.5 bg-red-500 text-white text-xs font-bold rounded-lg shadow-sm hover:bg-red-600 transition-colors flex items-center gap-1"
                                                    >
                                                        <Trash2 size={12} /> DELETE
                                                    </button>
                                                    <ChevronLeft className={`transition-transform ${expandedPackageId === pkg.id ? '-rotate-90' : 'rotate-180'}`} size={16} />
                                                </div>
                                            </div>

                                            {expandedPackageId === pkg.id && (
                                                <div className="p-4 border-t border-white/5 bg-black/20">
                                                    {loadingCards ? (
                                                        <div className="text-center py-4 text-xs text-white/40">Loading cards...</div>
                                                    ) : (
                                                        <div className="space-y-4">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setUploadMode('in_package');
                                                                    setSelectedPackageId(pkg.id);
                                                                    setCards([]);
                                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                }}
                                                                className="w-full py-3 border border-dashed border-[#3390EC]/30 bg-[#3390EC]/5 hover:bg-[#3390EC]/10 text-[#3390EC] rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
                                                            >
                                                                <Plus size={16} /> Add New Card to {pkg.name}
                                                            </button>

                                                            <div className="grid grid-cols-2 gap-4">
                                                                {packageCards.map(card => (
                                                                    <div key={card.id} className="bg-white/5 p-4 rounded-xl flex items-center justify-between group">
                                                                        <div className="scale-[0.8] origin-left pointer-events-none -my-6">
                                                                            <CardRenderer
                                                                                type="HOME"
                                                                                username="User"
                                                                                displayName={card.name}
                                                                                price={100}
                                                                                profileImage={card.image_filename}
                                                                                waveColor={card.base_color || pkg.default_color}
                                                                                textColor={card.text_color || pkg.text_color}
                                                                                onBuy={() => { }}
                                                                                hideLike={true}
                                                                            />
                                                                        </div>
                                                                        <div className="flex flex-col items-end gap-2">
                                                                            <div className="flex flex-col items-end gap-1 mb-2">
                                                                                <span className="text-[10px] font-mono text-white/60">Minted: {card.total_minted || 0} / {card.quantity > 0 ? card.quantity : '∞'}</span>
                                                                                {card.quantity > 0 && (card.quantity - (card.total_minted || 0)) <= 10 && (
                                                                                    <span className="text-[9px] font-black tracking-widest uppercase bg-red-500/20 text-red-500 px-2 py-0.5 rounded border border-red-500/30 shadow-[0_0_10px_rgba(239,68,68,0.2)]">
                                                                                        {(card.quantity - (card.total_minted || 0)) <= 0 ? 'Depleted' : 'Low Stock'}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); handleDeleteCardClick(card.id); }}
                                                                                className="bg-red-500/20 hover:bg-red-500 text-red-500 hover:text-white p-3 rounded-xl transition-all"
                                                                            >
                                                                                <Trash2 size={16} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                                {packageCards.length === 0 && <div className="col-span-2 text-center text-xs text-white/40">No cards in this package.</div>}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* STEP 1: UPLOAD (Only show in new_package or in_package modes) */}
                            {(uploadMode === 'new_package' || uploadMode === 'in_package') && (
                                <section className="space-y-4">
                                    <h2 className="text-tg-hint uppercase text-xs font-bold tracking-wider flex items-center gap-2">
                                        Step 1: Upload Assets
                                    </h2>

                                    {/* Cover Image Upload (Only New) */}
                                    {uploadMode === 'new_package' && (
                                        <div className="bg-tg-secondary border border-tg-text/5 rounded-2xl p-4 flex items-center gap-4">
                                            <div className={`w-20 h-20 rounded-xl flex items-center justify-center relative overflow-hidden ${coverImage ? 'border-0' : 'border-2 border-dashed border-tg-text/5'}`}>
                                                {coverImage ? (
                                                    <img src={coverImage} className="w-full h-full object-cover" />
                                                ) : (
                                                    <Package size={24} className="text-tg-hint" />
                                                )}
                                                <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleCoverUpload} />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-bold text-tg-text">Package Cover</h3>
                                                <p className="text-xs text-tg-hint mb-2">Required for the catalog.</p>
                                                <label className="bg-tg-bg px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer hover:bg-tg-text/5 transition-colors text-tg-text">
                                                    {coverImage ? 'Change Cover' : 'Upload Cover'}
                                                    <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
                                                </label>
                                            </div>
                                        </div>
                                    )}

                                    {/* Existing Package Select */}
                                    {uploadMode === 'in_package' && (
                                        <div className="bg-tg-secondary border border-tg-text/5 rounded-2xl p-4">
                                            <label className="block text-xs text-tg-hint mb-1.5">Select Target Package</label>
                                            <select
                                                value={selectedPackageId}
                                                onChange={e => setSelectedPackageId(e.target.value)}
                                                className="w-full bg-tg-bg rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:ring-2 ring-blue-500/50 appearance-none text-tg-text"
                                            >
                                                <option value="">Select a package...</option>
                                                {existingPackages.map(pkg => (
                                                    <option key={pkg.id} value={pkg.id}>
                                                        {pkg.name} ({pkg.total_supply} cards)
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Card Images Upload */}
                                    <div className="border-2 border-dashed border-blue-500/30 bg-blue-500/5 rounded-2xl h-32 flex flex-col items-center justify-center text-blue-500 gap-2 relative overflow-hidden transition-all hover:bg-blue-500/10">
                                        <Upload size={24} />
                                        <p className="text-sm font-bold">Upload Card Images</p>
                                        <p className="text-xs opacity-60">Select multiple files to start</p>
                                        <input type="file" multiple accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleImageUpload} />
                                    </div>
                                </section>
                            )}


                            {/* STEP 2: METADATA & EDITING */}
                            {hasUploadedCards && (
                                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

                                    {/* Package Settings (Only New) */}
                                    {uploadMode === 'new_package' && (
                                        <section className="space-y-4">
                                            <h2 className="text-tg-hint uppercase text-xs font-bold tracking-wider flex items-center gap-2">
                                                <Settings size={12} /> Package Configuration
                                            </h2>
                                            <div className="bg-tg-secondary rounded-2xl p-5 border border-tg-text/5 space-y-4">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs text-tg-hint mb-1.5">Package Name</label>
                                                        <input
                                                            type="text"
                                                            value={packageName}
                                                            onChange={e => setPackageName(e.target.value)}
                                                            className="w-full bg-tg-bg rounded-xl px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 ring-blue-500/50 text-tg-text"
                                                            placeholder="e.g. Season 1"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-tg-hint mb-1.5">Character Name</label>
                                                        <input
                                                            type="text"
                                                            value={characterName}
                                                            onChange={e => setCharacterName(e.target.value)}
                                                            className="w-full bg-tg-bg rounded-xl px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 ring-blue-500/50 text-tg-text"
                                                            placeholder="e.g. Forest Guardian"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                    <div>
                                                        <label className="block text-xs text-tg-hint mb-1.5">Total Supply</label>
                                                        <input
                                                            type="number"
                                                            value={totalSupply}
                                                            onChange={e => setTotalSupply(Number(e.target.value))}
                                                            className="w-full bg-tg-bg rounded-xl px-4 py-2.5 text-sm font-medium focus:outline-none text-tg-text"
                                                        />
                                                    </div>
                                                    <div className="flex items-end">
                                                        <button
                                                            onClick={() => setIsFree(!isFree)}
                                                            className={`w-full py-2.5 rounded-xl text-sm font-bold border transition-all ${isFree ? 'bg-green-500/20 text-green-500 border-green-500/30' : 'bg-tg-bg text-tg-hint border-transparent'}`}
                                                        >
                                                            {isFree ? 'FREE DROP' : 'PAID PACK'}
                                                        </button>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-tg-hint mb-1.5">Base Color</label>
                                                        <div className="flex items-center gap-2 bg-tg-bg rounded-xl px-2 py-1.5">
                                                            <input type="color" value={baseColor} onChange={e => setBaseColor(e.target.value)} className="w-6 h-6 rounded bg-transparent border-none" />
                                                            <input type="text" value={baseColor} onChange={e => setBaseColor(e.target.value)} className="bg-transparent text-[10px] w-full focus:outline-none uppercase font-mono text-tg-text" />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-tg-hint mb-1.5">Text Color</label>
                                                        <div className="flex items-center gap-2 bg-tg-bg rounded-xl px-2 py-1.5">
                                                            <input type="color" value={baseTextColor} onChange={e => setBaseTextColor(e.target.value)} className="w-6 h-6 rounded bg-transparent border-none" />
                                                            <input type="text" value={baseTextColor} onChange={e => setBaseTextColor(e.target.value)} className="bg-transparent text-[10px] w-full focus:outline-none uppercase font-mono text-tg-text" />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* NEW USER GIFT OPTION */}
                                                <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 flex items-center justify-between">
                                                    <div>
                                                        <h4 className="text-sm font-bold text-tg-text">New User Starter Pack</h4>
                                                        <p className="text-[10px] text-tg-hint">Users will receive 5 random cards from this pack upon first entry.</p>
                                                    </div>
                                                    <button
                                                        onClick={() => setIsNewUserGift(!isNewUserGift)}
                                                        className={`px-4 py-2 rounded-lg text-xs font-black transition-all ${isNewUserGift ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20' : 'bg-white/5 text-white/40'}`}
                                                    >
                                                        {isNewUserGift ? 'ENABLED' : 'DISABLED'}
                                                    </button>
                                                </div>
                                            </div>
                                        </section>
                                    )}

                                    {/* Card List Editor */}
                                    <section className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-tg-hint uppercase text-xs font-bold tracking-wider flex items-center gap-2">
                                                <ImageIcon size={12} /> Edited Cards ({cards.length})
                                            </h2>
                                            <label className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-colors flex items-center gap-2">
                                                <Plus size={14} /> Add More
                                                <input type="file" multiple accept="image/*" className="hidden" onChange={handleImageUpload} />
                                            </label>
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                            {cards.map((card) => (
                                                <div key={card.id} className="bg-tg-secondary p-4 rounded-2xl border border-tg-text/5 flex gap-6 items-start">

                                                    {/* PREVIEW: The Actual Card Component */}
                                                    <div className="flex-shrink-0 relative group">
                                                        <div className="scale-[0.8] origin-top-left -mr-4 -mb-8 pointer-events-none">
                                                            <CardRenderer
                                                                type="HOME"
                                                                username="Preview"
                                                                displayName={card.name || 'Card Name'}
                                                                price={100}
                                                                profileImage={card.previewUrl}
                                                                waveColor={card.color}
                                                                textColor={card.textColor || baseTextColor}
                                                                onBuy={() => { }}
                                                                hideLike={true}
                                                            />
                                                        </div>
                                                        {/* Overlay Delete Button */}
                                                        <button
                                                            onClick={() => removeCard(card.id)}
                                                            className="absolute -top-2 -right-2 bg-red-500 text-white p-1.5 rounded-full shadow-lg z-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </div>

                                                    {/* EDIT FORM */}
                                                    <div className="flex-1 space-y-4 pt-1">
                                                        {/* HEADER: Name, Price, Type */}
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={card.name}
                                                                onChange={e => updateCard(card.id, 'name', e.target.value)}
                                                                className="flex-1 bg-transparent border-b border-white/5 pb-1 text-sm font-bold focus:border-[#3390EC] focus:outline-none"
                                                                placeholder="Card Name"
                                                            />
                                                            <button
                                                                onClick={() => updateCard(card.id, 'isFree', !card.isFree)}
                                                                className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${card.isFree ? 'bg-green-500/20 text-green-500' : 'bg-white/5 text-white/40'}`}
                                                            >
                                                                {card.isFree ? 'Free' : 'Paid'}
                                                            </button>
                                                        </div>

                                                        {/* SECTION 1: FINANCIAL & MARKET */}
                                                        <div className="bg-black/20 p-3 rounded-xl border border-white/5 space-y-3">
                                                            <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-wider">Financial & Market</h4>
                                                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                                                                {!card.isFree && (
                                                                    <div>
                                                                        <label className="text-[10px] text-white/30 block mb-1">Price (GRAM)</label>
                                                                        <input
                                                                            type="number"
                                                                            value={card.price}
                                                                            onChange={e => updateCard(card.id, 'price', Number(e.target.value))}
                                                                            className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-xs focus:outline-none font-mono text-[#3390EC]"
                                                                            placeholder="0.00"
                                                                            step="0.1"
                                                                        />
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Royalty %</label>
                                                                    <input
                                                                        type="number"
                                                                        value={card.royaltyFee}
                                                                        onChange={e => updateCard(card.id, 'royaltyFee', Number(e.target.value))}
                                                                        className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-xs focus:outline-none font-mono"
                                                                        placeholder="0"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Listing Type</label>
                                                                    <select
                                                                        value={card.listingType}
                                                                        onChange={e => updateCard(card.id, 'listingType', e.target.value)}
                                                                        className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-[10px] focus:outline-none appearance-none"
                                                                    >
                                                                        <option>Market Only</option>
                                                                        <option>Auction Only</option>
                                                                        <option>Both</option>
                                                                        <option>Showcase</option>
                                                                    </select>
                                                                </div>
                                                                <div className="flex items-end">
                                                                    <button
                                                                        onClick={() => updateCard(card.id, 'isTradable', !card.isTradable)}
                                                                        className={`w-full py-1.5 rounded-lg text-[10px] font-bold border transition-all ${card.isTradable ? 'bg-[#3390EC]/10 text-[#3390EC] border-[#3390EC]/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`}
                                                                    >
                                                                        {card.isTradable ? 'Tradable' : 'Locked'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* SECTION 2: GAMEPLAY & SUPPLY */}
                                                        <div className="bg-black/20 p-3 rounded-xl border border-white/5 space-y-3">
                                                            <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-wider">Gameplay & Supply</h4>
                                                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Quantity</label>
                                                                    <input
                                                                        type="number"
                                                                        value={card.quantity}
                                                                        onChange={e => updateCard(card.id, 'quantity', Number(e.target.value))}
                                                                        className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-xs focus:outline-none font-mono"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Rarity (0-100)</label>
                                                                    <input
                                                                        type="number"
                                                                        value={card.rarity}
                                                                        onChange={e => updateCard(card.id, 'rarity', Number(e.target.value))}
                                                                        className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-xs focus:outline-none font-mono"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Rank</label>
                                                                    <select
                                                                        value={card.rank}
                                                                        onChange={e => updateCard(card.id, 'rank', e.target.value)}
                                                                        className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-[10px] focus:outline-none appearance-none"
                                                                    >
                                                                        <option>Bronze</option>
                                                                        <option>Silver</option>
                                                                        <option>Gold</option>
                                                                        <option>Platinum</option>
                                                                        <option>Diamond</option>
                                                                    </select>
                                                                </div>
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Drop Rate %</label>
                                                                    <input
                                                                        type="number"
                                                                        value={card.dropRate}
                                                                        onChange={e => updateCard(card.id, 'dropRate', Number(e.target.value))}
                                                                        className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-xs focus:outline-none font-mono"
                                                                        step="0.01"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="grid grid-cols-1">
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Required Access</label>
                                                                    <select
                                                                        value={card.accessLevel}
                                                                        onChange={e => updateCard(card.id, 'accessLevel', Number(e.target.value))}
                                                                        className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-[10px] focus:outline-none appearance-none"
                                                                    >
                                                                        <option value={1}>Level 1: Public</option>
                                                                        <option value={2}>Level 2: Premium</option>
                                                                        <option value={3}>Level 3: VIP</option>
                                                                        <option value={4}>Level 4: Ultra</option>
                                                                        <option value={5}>Level 5: Legend</option>
                                                                        <option value={6}>Level 6: Founder</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* SECTION 3: VISUALS */}
                                                        <div className="bg-black/20 p-3 rounded-xl border border-white/5 space-y-3">
                                                            <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-wider">Visuals & Variants</h4>
                                                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Background</label>
                                                                    <div className="flex items-center gap-2 rounded-lg bg-[#2c2c2e] px-2 py-1.5">
                                                                        <input type="color" value={card.color} onChange={e => updateCard(card.id, 'color', e.target.value)} className="w-4 h-4 rounded bg-transparent border-none flex-shrink-0" />
                                                                        <input type="text" value={card.color} onChange={e => updateCard(card.id, 'color', e.target.value)} className="bg-transparent text-[10px] font-mono text-white/60 w-full focus:outline-none uppercase" />
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Text Color</label>
                                                                    <div className="flex items-center gap-2 rounded-lg bg-[#2c2c2e] px-2 py-1.5">
                                                                        <input type="color" value={card.textColor} onChange={e => updateCard(card.id, 'textColor', e.target.value)} className="w-4 h-4 rounded bg-transparent border-none flex-shrink-0" />
                                                                        <input type="text" value={card.textColor} onChange={e => updateCard(card.id, 'textColor', e.target.value)} className="bg-transparent text-[10px] font-mono text-white/60 w-full focus:outline-none uppercase" />
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Icon Color</label>
                                                                    <div className="flex items-center gap-2 rounded-lg bg-[#2c2c2e] px-2 py-1.5">
                                                                        <input type="color" value={card.iconColor} onChange={e => updateCard(card.id, 'iconColor', e.target.value)} className="w-4 h-4 rounded bg-transparent border-none flex-shrink-0" />
                                                                        <input type="text" value={card.iconColor} onChange={e => updateCard(card.id, 'iconColor', e.target.value)} className="bg-transparent text-[10px] font-mono text-white/60 w-full focus:outline-none uppercase" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Special FX</label>
                                                                    <select
                                                                        value={card.specialFx}
                                                                        onChange={e => updateCard(card.id, 'specialFx', e.target.value)}
                                                                        className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-[10px] focus:outline-none appearance-none"
                                                                    >
                                                                        <option>None</option>
                                                                        <option>Glow</option>
                                                                        <option>Neon</option>
                                                                        <option>Particles</option>
                                                                    </select>
                                                                </div>
                                                                <div>
                                                                    <label className="text-[10px] text-white/30 block mb-1">Skin Label</label>
                                                                    <input
                                                                        type="text"
                                                                        value={card.skinLabel}
                                                                        onChange={e => updateCard(card.id, 'skinLabel', e.target.value)}
                                                                        className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-[10px] focus:outline-none"
                                                                        placeholder="e.g. Holo"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] text-white/30 block mb-1">Variant Group ID</label>
                                                                <input
                                                                    type="text"
                                                                    value={card.variantGroupId}
                                                                    onChange={e => updateCard(card.id, 'variantGroupId', e.target.value)}
                                                                    className="w-full bg-[#2c2c2e] rounded-lg px-2 py-1.5 text-[10px] focus:outline-none font-mono"
                                                                    placeholder="Optional: Link related cards"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>

                                    {/* BOTTOM SAVE BUTTON */}
                                    <div className="pt-4 border-t border-white/5 pb-20">
                                        <button
                                            disabled={isSaving}
                                            onClick={handleSave}
                                            className={`w-full py-4 text-white rounded-xl font-bold text-lg shadow-lg flex items-center justify-center gap-2 transition-all ${isSaving ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-[#3390EC] hover:bg-[#3390EC]/90 active:scale-[0.98]'}`}
                                        >
                                            {isSaving ? (
                                                <RefreshCw size={20} className="animate-spin" />
                                            ) : (
                                                <Save size={20} />
                                            )}
                                            {isSaving ? 'Saving Package...' : 'Save Changes'}
                                        </button>
                                    </div>
                                </div>
                            )}


                            {uploadMode === 'skins' && (
                                <SkinsManager onBack={() => setUploadMode('overview')} />
                            )}
                            {uploadMode === 'wallet_skins' && (
                                <WalletCardsManager onBack={() => setUploadMode('overview')} />
                            )}

                            {uploadMode === 'ui_config' && (
                                <UISettingsManager />
                            )}
                            
                            {uploadMode === 'gifting' && (
                                <GiftManager />
                            )}
                            {uploadMode === 'controller' && (
                                <ControllerManager />
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
            
            {/* Modals placed inside AdminDashboard to overlay properly */}
            <ConfirmActionModal
                isOpen={deleteCardConfirm !== null}
                onClose={() => setDeleteCardConfirm(null)}
                onConfirm={executeDeleteCard}
                title="Delete Card"
                message="Are you sure you want to permanently delete this card? This action cannot be undone."
                isDanger={true}
            />

            <ConfirmActionModal
                isOpen={deletePackageConfirm !== null}
                onClose={() => setDeletePackageConfirm(null)}
                onConfirm={executeDeletePackage}
                title="Delete Package"
                message="Are you sure you want to delete this package? All its contents will be lost."
                isDanger={true}
            />

            <ConfirmActionModal
                isOpen={truncateTableConfirm !== null}
                onClose={() => setTruncateTableConfirm(null)}
                onConfirm={executeTruncateTable}
                title="Truncate Table"
                message={`Are you sure you want to clear all records from ${truncateTableConfirm}?`}
                isDanger={true}
            />
        </div>
    );
};

export default AdminDashboard;
